import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ScreenshotView } from '@thottbot/shared';
import { apiSend, queryKeys, useMe } from '../../api/client';
import { LoginGate } from '../../components/LoginGate';
import { ReportButton } from './ReportButton';
import { describeError } from '../../pages/Submit';

interface Props {
  artifactId: string;
  artifactType: string;
  artifactSlug: string;
  screenshots: ScreenshotView[];
}

export function ScreenshotGallery({ artifactId, artifactType, artifactSlug, screenshots }: Props) {
  const qc = useQueryClient();
  const me = useMe();
  const user = me.data?.user ?? null;
  const refresh = () =>
    qc.invalidateQueries({ queryKey: queryKeys.artifact(artifactType, artifactSlug) });
  const [error, setError] = useState<string | null>(null);

  const remove = async (id: string) => {
    setError(null);
    try {
      await apiSend('DELETE', `/api/screenshots/${id}`);
      refresh();
    } catch (err) {
      setError(describeError(err));
    }
  };

  return (
    <div>
      {screenshots.length === 0 ? (
        <p className="muted">No screenshots yet.</p>
      ) : (
        <div className="gallery" data-testid="gallery">
          {screenshots.map((s) => (
            <figure key={s.id} data-testid="screenshot">
              <a href={s.url} target="_blank" rel="noreferrer">
                <img
                  src={s.url}
                  alt={s.caption ?? `Screenshot by ${s.uploaderBattletag}`}
                  loading="lazy"
                />
              </a>
              <figcaption>
                {s.caption && <span>{s.caption} </span>}
                <span>by {s.uploaderBattletag}</span>
                {user && (user.id === s.uploaderId || user.role !== 'player') && (
                  <>
                    {' · '}
                    <button
                      type="button"
                      className="vote-arrow"
                      style={{ color: 'var(--muted)' }}
                      onClick={() => remove(s.id)}
                    >
                      delete
                    </button>
                  </>
                )}
                {user && user.id !== s.uploaderId && (
                  <>
                    {' · '}
                    <ReportButton targetType="screenshot" targetId={s.id} />
                  </>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      {error && <div className="error">{error}</div>}
      <LoginGate action="add a screenshot">
        <UploadForm artifactId={artifactId} onUploaded={refresh} />
      </LoginGate>
    </div>
  );
}

/** Multipart upload through XMLHttpRequest so we can show progress. */
function UploadForm({ artifactId, onUploaded }: { artifactId: string; onUploaded: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [caption, setCaption] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = (e: { preventDefault(): void }) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError('Choose a JPEG or PNG first.');
      return;
    }
    setError(null);
    const form = new FormData();
    form.append('caption', caption);
    form.append('file', file);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/artifacts/${artifactId}/screenshots`);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
    };
    xhr.onload = () => {
      setProgress(null);
      if (xhr.status >= 200 && xhr.status < 300) {
        setCaption('');
        if (fileRef.current) fileRef.current.value = '';
        onUploaded();
      } else {
        let message = `Upload failed (${xhr.status})`;
        try {
          const body = JSON.parse(xhr.responseText) as { message?: string; error?: string };
          message = body.message ?? body.error ?? message;
        } catch {
          /* non-JSON error body */
        }
        setError(message);
      }
    };
    xhr.onerror = () => {
      setProgress(null);
      setError('Upload failed (network error)');
    };
    setProgress(0);
    xhr.send(form);
  };

  return (
    <form className="section" aria-label="Screenshot upload" onSubmit={submit}>
      <div className="form-row">
        <label htmlFor="shot-file">Add screenshot</label>
        <input id="shot-file" ref={fileRef} type="file" accept="image/jpeg,image/png" />
      </div>
      <div className="form-row">
        <label htmlFor="shot-caption">Caption</label>
        <input
          id="shot-caption"
          type="text"
          value={caption}
          maxLength={200}
          onChange={(e) => setCaption(e.target.value)}
        />
      </div>
      {progress !== null && (
        <div
          className="muted"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          Uploading... {progress}%
        </div>
      )}
      {error && (
        <div className="notice error" role="alert">
          {error}
        </div>
      )}
      <div className="actions">
        <button type="submit" disabled={progress !== null}>
          Upload
        </button>
        <span className="muted">JPEG or PNG, 5 MB or smaller.</span>
      </div>
    </form>
  );
}
